/**
 * Feature flags configuration
 * Used to control feature rollout and backwards compatibility
 */

export interface FeatureFlags {
  // Terminal-related features
  terminals: boolean;
  devicesAlias: boolean; // Backwards compatibility for device endpoints
  hardwareVitalsOnTerminal: boolean; // Show battery/wifi on terminal cards
  
  // Future features
  advancedAnalytics: boolean;
  realTimeMonitoring: boolean;
}

// Default feature flags
export const defaultFeatureFlags: FeatureFlags = {
  terminals: true,
  devicesAlias: true, // Keep enabled until clients migrate
  hardwareVitalsOnTerminal: false, // Hide hardware vitals by default
  advancedAnalytics: false,
  realTimeMonitoring: false,
};

// Environment-based overrides
const getFeatureFlags = (): FeatureFlags => {
  const env = process.env.NODE_ENV;
  
  // Development overrides
  if (env === 'development') {
    return {
      ...defaultFeatureFlags,
      advancedAnalytics: true, // Enable in dev for testing
    };
  }
  
  // Production settings
  return defaultFeatureFlags;
};

export const featureFlags = getFeatureFlags();

/**
 * Helper function to check if a feature is enabled
 */
export const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
  return featureFlags[feature];
};

/**
 * React hook for feature flags
 */
export const useFeatureFlag = (feature: keyof FeatureFlags): boolean => {
  return isFeatureEnabled(feature);
};
